import cn from 'classnames';
import { forwardRef, HTMLProps, PropsWithChildren, useImperativeHandle, useRef } from 'react';
import s from './input-file.module.css';

type FileType = '.txt' | '.json' | '.png' | '.jpeg' | 'image/*' | '.pdf' | 'video/*';

export interface InputFileProps
  extends PropsWithChildren,
    Omit<HTMLProps<HTMLInputElement>, 'onChange'> {
  onChange?: (data: File[]) => void;
  types: FileType[];
  wrapperClassName?: string;
  maxFileSizeInMb?: number;
  onFileSizeError?: (file: File) => void;
}

export const InputFile = forwardRef<HTMLInputElement, InputFileProps>((props, ref) => {
  const {
    onChange,
    children,
    types,
    wrapperClassName,
    disabled,
    maxFileSizeInMb,
    onFileSizeError,
    name,
    ...others
  } = props;

  const inputRef = useRef<HTMLInputElement>(null);

  useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

  const handleSelectFile = (fileList: FileList | null) => {
    if (fileList) {
      const files = Array.from(fileList).filter((file) => {
        if (maxFileSizeInMb) {
          const maxFileSize = maxFileSizeInMb * 1024 * 1024;
          const isBigger = file.size > maxFileSize;
          if (isBigger) onFileSizeError?.(file);
          return !isBigger;
        }
        return true;
      });
      files.length && onChange?.(files);
    }
  };

  const acceptTypes = types.join(', ');

  return (
    <div
      onClick={() => onChange && inputRef.current?.click()}
      data-name={name}
      className={cn(wrapperClassName, s.component, disabled && s.disabled)}
    >
      {children}
      <input
        type="file"
        accept={acceptTypes}
        onChange={(e) => handleSelectFile(e.target.files)}
        ref={inputRef}
        role="textbox"
        style={{ display: 'none' }}
        {...others}
      />
    </div>
  );
});

InputFile.displayName = 'InputFile';
